import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { ShoppingBag, Mail, Lock, AlertCircle, ShieldCheck, ArrowLeft } from "lucide-react";

export default function Login() {
  const { user, login, verify2FA, error, setError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  // 2FA step state (populated once /auth/login returns requires_2fa)
  const [pending, setPending] = useState(null); // { pending_token, email }
  const [code, setCode] = useState("");

  if (user === undefined) return null;
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await login(email.trim(), password);
      if (res && res.requires_2fa) {
        setPending({ pending_token: res.pending_token, email: res.email || email });
      }
    } catch (e) { /* shown by ctx */ }
    finally { setBusy(false); }
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Derive a friendly device label from UA if available.
      const label = typeof navigator !== "undefined" ? (navigator.userAgent || "").slice(0, 100) : "";
      await verify2FA(pending.pending_token, code.trim(), label);
    } catch (e) { /* shown by ctx */ }
    finally { setBusy(false); }
  };

  const backToPassword = () => {
    setPending(null);
    setCode("");
    setError && setError("");
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950 px-4">
      <div className="pointer-events-none fixed inset-0 opacity-[0.04]" style={{
        backgroundImage: "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
        backgroundSize: "48px 48px",
      }} />
      <div className="relative w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-3 inline-flex items-center gap-3">
            <div className="rounded-xl bg-gradient-to-br from-orange-500 to-rose-500 p-2.5 shadow-lg shadow-orange-500/30">
              <ShoppingBag className="h-6 w-6 text-white" />
            </div>
            <div className="text-left">
              <h1 className="text-2xl font-bold text-zinc-50">BragusApp</h1>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Commerce Bridge</p>
            </div>
          </div>
        </div>

        {!pending ? (
          <form onSubmit={submit} data-testid="login-form" className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-6 backdrop-blur">
            <h2 className="mb-1 text-lg font-semibold text-zinc-100">Autentificare</h2>
            <p className="mb-5 text-xs text-zinc-500">Introdu email-ul și parola pentru a continua.</p>

            {error && (
              <div className="mb-4 flex items-start gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5" /><span>{error}</span>
              </div>
            )}

            <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Email</label>
            <div className="mb-3 flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2">
              <Mail className="h-4 w-4 text-zinc-500" />
              <input data-testid="login-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-transparent text-sm text-zinc-100 placeholder-zinc-600 outline-none" placeholder="nume@email.com" />
            </div>

            <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Parolă</label>
            <div className="mb-5 flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2">
              <Lock className="h-4 w-4 text-zinc-500" />
              <input data-testid="login-password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="flex-1 bg-transparent text-sm text-zinc-100 placeholder-zinc-600 outline-none" placeholder="••••••••" />
            </div>

            <button data-testid="login-submit" type="submit" disabled={busy}
              className="w-full rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 py-2.5 text-sm font-medium text-white shadow-lg shadow-orange-500/20 transition hover:from-orange-400 hover:to-rose-400 disabled:opacity-50">
              {busy ? "Se autentifică..." : "Autentificare"}
            </button>

            <p className="mt-4 text-center text-[10px] text-zinc-600">
              BragusApp v0.3 · doar utilizatorii invitați au acces
            </p>
          </form>
        ) : (
          <form onSubmit={submit2FA} data-testid="verify-2fa-form" className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-6 backdrop-blur">
            <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-300">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <h2 className="mb-1 text-lg font-semibold text-zinc-100">Verificare 2FA</h2>
            <p className="mb-5 text-xs text-zinc-500">
              Deschide aplicația de autentificare (Google Authenticator, Authy, 1Password) și introdu codul de 6 cifre pentru <span className="text-zinc-300">{pending.email}</span>.
            </p>

            {error && (
              <div className="mb-4 flex items-start gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5" /><span>{error}</span>
              </div>
            )}

            <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Cod 2FA</label>
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2">
              <ShieldCheck className="h-4 w-4 text-zinc-500" />
              <input
                data-testid="verify-2fa-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="flex-1 bg-transparent text-lg tracking-widest text-zinc-100 placeholder-zinc-600 outline-none"
                placeholder="123456"
              />
            </div>

            <p className="mb-4 text-[11px] text-zinc-500">
              Ai pierdut telefonul? Introdu unul din codurile de rezervă (formatul <code className="text-zinc-300">XXXX-XXXX</code>).
            </p>

            <button data-testid="verify-2fa-submit" type="submit" disabled={busy || code.trim().length < 6}
              className="w-full rounded-lg bg-gradient-to-b from-emerald-500 to-emerald-600 py-2.5 text-sm font-medium text-white shadow-lg shadow-emerald-500/20 transition hover:from-emerald-400 hover:to-emerald-500 disabled:opacity-50">
              {busy ? "Se verifică..." : "Verifică și continuă"}
            </button>

            <button type="button" onClick={backToPassword} disabled={busy}
              data-testid="verify-2fa-back"
              className="mt-3 inline-flex w-full items-center justify-center gap-1 rounded-lg border border-zinc-800 py-2 text-xs text-zinc-400 hover:bg-zinc-900">
              <ArrowLeft className="h-3.5 w-3.5" /> Înapoi la parolă
            </button>

            <p className="mt-4 text-center text-[10px] text-zinc-600">
              Acest dispozitiv va fi memorat 30 de zile după verificare.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
